import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FiLink, FiX } from 'react-icons/fi';
import { extractApiErrorMessage } from '@/lib/api';
import { track } from '@/lib/analytics';
import { CLIO_BLUE, CLIO_BLUE_SOFT } from '@/lib/clio-brand';
import {
  disconnectClio,
  fetchClioStatus,
  startClioAuthorize,
} from '@/lib/clio';

const FONT =
  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

/**
 * Dashboard card: connect / disconnect Clio Manage for counsel report push.
 */
export function ClioIntegrationCard() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const queryClient = useQueryClient();
  const [banner, setBanner] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const clio = params.get('clio');
    if (!clio) return;
    if (clio === 'connected') {
      setBanner('Clio connected. Open a client to send counsel reports to a matter.');
      track('Clio Connected', { source: 'pro_portal' });
      queryClient.invalidateQueries({ queryKey: ['clio-status'] });
    } else if (clio === 'error') {
      const reason = params.get('reason') || 'unknown';
      setError(`Could not connect Clio (${reason}). Try again or contact Harbor support.`);
    }
    params.delete('clio');
    params.delete('reason');
    const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
    window.history.replaceState({}, '', next);
  }, [queryClient]);

  const statusQuery = useQuery({
    queryKey: ['clio-status'],
    queryFn: () => fetchClioStatus(getToken),
    enabled: isLoaded && !!isSignedIn,
    staleTime: 60_000,
  });

  const connectMutation = useMutation({
    mutationFn: async () => {
      track('Clio Connect Clicked', { source: 'pro_portal' });
      const url = await startClioAuthorize(getToken);
      window.location.href = url;
    },
    onError: (e: unknown) => {
      setError(extractApiErrorMessage(e, 'Could not start Clio connection.'));
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => disconnectClio(getToken),
    onSuccess: () => {
      track('Clio Disconnected', { source: 'pro_portal' });
      setBanner('Clio disconnected.');
      queryClient.invalidateQueries({ queryKey: ['clio-status'] });
    },
    onError: (e: unknown) => {
      setError(extractApiErrorMessage(e, 'Could not disconnect Clio.'));
    },
  });

  if (statusQuery.isLoading) {
    return null;
  }

  const status = statusQuery.data;
  if (!status?.configured && !status?.connected) {
    // Hide entirely when env secrets are not set (local/dev without Clio app).
    return null;
  }

  return (
    <section className='mb-8' style={{ fontFamily: FONT }}>
      <div
        className='rounded-2xl border bg-white p-5 shadow-sm'
        style={{ borderColor: `${CLIO_BLUE}44` }}
      >
        <div className='flex flex-wrap items-start justify-between gap-3'>
          <div>
            <h2 className='flex items-center gap-2 text-base font-semibold text-gray-900'>
              <span
                className='inline-flex h-7 w-7 items-center justify-center rounded-lg'
                style={{ backgroundColor: CLIO_BLUE_SOFT, color: CLIO_BLUE }}
              >
                <FiLink className='h-4 w-4' />
              </span>
              Clio Manage
            </h2>
            <p className='mt-1 text-sm text-gray-600'>
              Send counsel PDFs (compliance, expense disputes, hearing prep)
              directly onto a Clio matter, with no download/re-upload.
            </p>
            {status.connected ? (
              <p className='mt-2 text-xs font-semibold text-emerald-700'>
                Connected
                {status.clio_user_name ? ` as ${status.clio_user_name}` : ''}
              </p>
            ) : (
              <p className='mt-2 text-xs text-gray-500'>Not connected</p>
            )}
          </div>
          <div className='flex flex-wrap gap-2'>
            {status.connected ? (
              <button
                type='button'
                disabled={disconnectMutation.isPending}
                onClick={() => {
                  if (window.confirm('Disconnect Clio from Harbor?')) {
                    setError(null);
                    disconnectMutation.mutate();
                  }
                }}
                className='inline-flex items-center gap-1.5 rounded-xl border border-gray-300 px-3.5 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50'
              >
                <FiX className='h-3.5 w-3.5' />
                {disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
              </button>
            ) : (
              <button
                type='button'
                disabled={connectMutation.isPending || !status.configured}
                onClick={() => {
                  setError(null);
                  connectMutation.mutate();
                }}
                className='inline-flex items-center gap-1.5 rounded-xl bg-[#0070E0] px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[#0F478D] disabled:opacity-50'
              >
                <FiLink className='h-3.5 w-3.5' />
                {connectMutation.isPending ? 'Redirecting…' : 'Connect Clio'}
              </button>
            )}
          </div>
        </div>
        {banner ? (
          <p className='mt-3 text-xs text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2'>
            {banner}
          </p>
        ) : null}
        {error ? (
          <p className='mt-3 text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2'>
            {error}
          </p>
        ) : null}
      </div>
    </section>
  );
}
